import { useCallback, useEffect, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import type { Campaign } from "@/hooks/api/use-campaigns";
import {
  useTrackedBrowserStorage,
  useTrackedLocalStorage,
} from "@/hooks/use-tracked-browser-storage";

// Constants
const MAX_RECENT_CAMPAIGNS = 10;

// Minimal type definition for a recent campaign with only essential fields for storage
export interface RecentCampaign {
  id: string;
  name: string;
  source_record_type: string;
  timestamp: number;
}

/**
 * Hook for managing campaign selection with automatic organization scoping.
 *
 * Features:
 * - Selects campaigns and persists them in URL parameters and localStorage
 * - Tracks recently selected campaigns with automatic organization isolation
 * - Synchronizes campaign selection between URL and recent campaigns
 * - Falls back to previously selected campaign when URL has no campaignId
 */
export function useCampaignSelection() {
  const params = useParams<{ campaignId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  // Manage selected campaign and recent campaigns with automatic organization scoping
  const [selectedCampaignId, setSelectedCampaignId] =
    useTrackedLocalStorage<string>(
      "campaigns::explorer::selected-campaign::active",
      "",
    );
  const [recentCampaigns, setRecentCampaigns] = useTrackedBrowserStorage<
    RecentCampaign[]
  >("campaigns::explorer::selected-campaign::recent", [], {
    storage: "localStorage",
    migration: {
      from: {
        key: "recentCampaigns",
        storage: "localStorage",
        organizationScoped: true,
      },
      cleanup: true,
    },
  });

  // Get campaign ID from URL path param only
  const campaignIdFromPath = params.campaignId;
  const storedCampaignId = selectedCampaignId || undefined;

  const currentSelectedCampaignId = campaignIdFromPath || storedCampaignId;

  // Sort recent campaigns by timestamp (most recent first)
  const sortedRecentCampaigns = useMemo(() => {
    return [...recentCampaigns].sort((a, b) => b.timestamp - a.timestamp);
  }, [recentCampaigns]);

  // Find the selected campaign in recent campaigns list
  const initialCampaign = useMemo(() => {
    if (!currentSelectedCampaignId || recentCampaigns.length === 0)
      return undefined;
    return recentCampaigns.find(
      (campaign) => campaign.id === currentSelectedCampaignId,
    );
  }, [currentSelectedCampaignId, recentCampaigns]);

  // Handle campaign selection
  const handleCampaignChange = useCallback(
    (value: string, campaign: Campaign | RecentCampaign | null) => {
      if (value) {
        // Preserve existing query params (e.g., ?view)
        const target = {
          pathname: `/campaigns/${value}/explorer`,
          search: location.search,
        };
        const isSameDestination =
          location.pathname === target.pathname &&
          location.search === (target.search || "");
        if (!isSameDestination) {
          navigate(target, { replace: true });
        }

        // Also update localStorage for persistence across page loads
        setSelectedCampaignId(value);

        // Add to recent campaigns if campaign object is provided
        if (campaign) {
          // Extract minimal fields and add with current timestamp (works for both full and recent objects)
          setRecentCampaigns((prev) => {
            const filtered = (prev || []).filter((c) => c.id !== campaign.id);
            return [
              {
                id: campaign.id,
                name: campaign.name,
                source_record_type: campaign.source_record_type,
                timestamp: Date.now(),
              },
              ...filtered,
            ].slice(0, MAX_RECENT_CAMPAIGNS);
          });
        }
      } else {
        // Remove from localStorage only
        setSelectedCampaignId("");
      }
    },
    [
      navigate,
      setSelectedCampaignId,
      setRecentCampaigns,
      location.pathname,
      location.search,
    ],
  );

  // Initialize URL from localStorage if needed
  useEffect(() => {
    // If we have a stored selection but no URL param, update the URL
    if (!campaignIdFromPath && storedCampaignId && selectedCampaignId) {
      if (location.pathname === "/campaigns/explorer") {
        // Spec: no auto-redirect from base explorer; leave blank until user selects
      }
    }
  }, [
    campaignIdFromPath,
    storedCampaignId,
    selectedCampaignId,
    location.pathname,
  ]);

  return {
    selectedCampaignId: currentSelectedCampaignId,
    initialCampaign,
    handleCampaignChange,
    recentCampaigns: sortedRecentCampaigns,
  };
}
